'use client';
import { groupBy, startCase, uniqBy } from 'lodash';
import {
  BookOpen,
  ChevronDown,
  Dot,
  Globe,
  Search,
  Shield,
  SquareCheckBig,
  Terminal,
} from 'lucide-react';
import { DeepThinkStatus } from '@/type/chat';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { getStatusIcon, getWebSiteDomain } from '../tools/utils';
import Link from 'next/link';
import Markdown from 'markdown-to-jsx';
// import SyntaxHighlightedCode from './SyntaxHighlightedCode';
import { useRightDrawer } from '@/contexts/DrawerContext';
import KnowledgeBaseView from './KnowledgeBaseView';
import { formatSentence } from '@/lib/utils/string';
import { Avatar, Collapse } from '@mui/material';
import { parseThinkingData } from '@/lib/utils/thinkingParser';
import StepContent from '../message/StepContent';
import { TransitionGroup } from 'react-transition-group';
import RagCanvasContainer from '@/components/DeepSearch/thinking/RagCanvasContainer';
import { DOMAttributes, useState, useRef } from 'react';
import ScrollingBroadcast from './ScrollingBroadcast';
import TaskProgressAnimation from './TaskProgressAnimation';
import { GridMotionBackground } from '@/components/Login/GridMotionBackground';
import { useTheme } from 'next-themes';

/**
 * 从 thinkStatus 数组中从后往前查找 message 属性值不在指定列表中的项
 * @param thinkStatus - DeepThinkStatus 数组
 * @returns 找到的第一个不在指定列表中的项，如果没有找到则返回 undefined
 */
function findLastNonMatchingMessage(thinkStatus: DeepThinkStatus[]): DeepThinkStatus | undefined {
  const excludedMessages = ['task_progress', 'tool_result', 'assistant_message'];

  // 从后往前遍历
  for (let i = thinkStatus.length - 1; i >= 0; i--) {
    const item = thinkStatus[i];
    if (item.message && !excludedMessages.includes(item.message)) {
      return item;
    }
  }

  return undefined;
}
interface Todo {
  status: 'in_progress' | 'pending' | 'completed' | 'doing' | 'done' | 'waiting' | 'error';
  content: string;
  fileName?: string;
  url?: string;
}
interface ThinkTasksProps {
  list: DeepThinkStatus[];
  todos: {
    [k: string]: Todo[];
  };
  in?: boolean;
  agentStatus: 'doing' | 'done' | 'waiting' | 'error';
}

export default function ThinkTaskCard(
  props: {
    title: string;
    status?: any;
    active: boolean;
    thinkStatus: DeepThinkStatus[];
  } & DOMAttributes<HTMLDivElement>,
) {
  const { title, status, active, thinkStatus, ...rest } = props;
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [checked, setChecked] = useState(true);
  const [completed, setCompleted] = useState(status === 'done');
  const progressRef = useRef(0);

  let progress =
    status === 'done'
      ? 100
      : thinkStatus.filter((item) => item.progress && item.progress >= 0).at(-1)?.progress ||
        progressRef.current;

  progressRef.current = progress;

  const lastMeaningfulStatus = findLastNonMatchingMessage(thinkStatus);

  const todos = serializeToolEvents(thinkStatus);

  const hasTodos = Object.keys(todos).length > 0;

  return (
    <div
      className="relative w-full overflow-hidden border-t dark:border-[#1E1E1E] border-[#EFEFEF]"
      {...rest}
    >
      <div className="flex items-center justify-between h-[70px] px-6">
        <div className="flex flex-col justify-center gap-1">
          <p className="font-medium text-sm leading-5 line-clamp-1">{startCase(title)}</p>
          {status === 'done' ? (
            <p className="flex text-xs leading-5 text-text-tertiary items-center">
              <Dot className="text-green-700 -ml-2.5" size={26} />
              {t('Completed')}
            </p>
          ) : (
            <ScrollingBroadcast
              content={
                lastMeaningfulStatus?.message
                  ? lastMeaningfulStatus?.message === 'Validate'
                    ? lastMeaningfulStatus.content || 'validating'
                    : lastMeaningfulStatus.message
                  : 'pending'
              }
              duration={0.6}
            />
          )}
        </div>
        {completed && hasTodos && (
          <ChevronDown
            className={cn('text-text-tertiary hover:text-[#A772FF] transition-all cursor-pointer', {
              'rotate-180': !checked,
            })}
            onClick={() => setChecked((c) => !c)}
          />
        )}
        {!completed && (
          <TaskProgressAnimation
            progress={progress}
            onFadeOutEnd={() => {
              setCompleted(true);
              setChecked(false);
            }}
            isDark={theme === 'dark'}
          />
        )}
      </div>
      {hasTodos && (
        <Collapse in={checked} className="w-full">
          <ThinkTasks todos={todos} list={thinkStatus} agentStatus={status} in={checked} />
        </Collapse>
      )}
    </div>
  );
}

function ThinkTasks({ list, todos, agentStatus, in: checked }: ThinkTasksProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { openDrawer, setDrawerWidth } = useRightDrawer();

  const handleKnowledgeView = (filename: string) => {
    setDrawerWidth(380);
    openDrawer(<KnowledgeBaseView fileName={filename} />, 'Knowledge Details');
  };

  const resultData = list.find((item) => item.status === 'done');

  // 使用统一的解析函数
  const parsedData = resultData
    ? parseThinkingData(
        resultData.message,
        resultData.userFriendlyInfo,
        resultData.thinkingContent,
        resultData.visualization,
      )
    : undefined;
  // console.log(groupBy(list, 'toolName'));
  // console.log(todos);
  if (Object.entries(todos).length === 0) {
    return null;
  }
  return (
    <div className="relative max-h-[560px] overflow-y-auto border-t dark:border-[#1E1E1E] border-[#EFEFEF]">
      <GridMotionBackground
        className="size-full max-h-[260px] absolute right-0 hidden lg:block z-[-1]"
        isDark={theme === 'dark'}
        enableSpaceStar={false}
      />
      <div className="relative px-6 pt-6 space-y-3 border-l dark:border-[#1E1E1E] border-[#EFEFEF] ml-7 mb-6">
        {Object.entries(todos).map(([toolName, items]) => (
          <div key={`${toolName}`}>
            <div className="capitalize flex items-center gap-2 -ml-8">
              {getToolIcon(toolName)}
              {t(startCase(toolName))}
            </div>
            <TransitionGroup
              className={cn('py-5 space-y-2 text-sm', {
                'text-text-secondary': ['todolist', 'validate'].includes(toolName.toLowerCase()),
                'rounded-xl overflow-hidden': toolName.toLowerCase() === 'codegeneration',
              })}
              appear
            >
              {items.map((item: Todo, index: number) => {
                // if (toolName === 'codeGeneration') {
                //   return (
                //     <div
                //       key={`${item.content}_${index}`}
                //       className="border border-[#1e1e1e] rounded-[12px] overflow-hidden"
                //     >
                //       <div className="flex items-center gap-2 h-8 px-4 bg-[#0a0a0a] text-xs text-text-tertiary">
                //         <Terminal size={14} className="text-[#67FFFF]" />
                //         {item.fileName}
                //       </div>
                //       <Markdown
                //         className="h-[268px] overflow-scroll bg-transparent"
                //         options={{ overrides: { code: SyntaxHighlightedCode } }}
                //       >{`\`\`\`python\n${item.content}\n\`\`\`\n`}</Markdown>
                //     </div>
                //   );
                // } else
                if (toolName === 'knowledgeBaseAnalysis') {
                  return (
                    <Collapse key={JSON.stringify(item.content)} in={checked}>
                      <RagCanvasContainer knowledgeGraph={item.content as any} />
                    </Collapse>
                  );
                } else if (toolName === 'validate') {
                  return (
                    <Collapse key={`${item.content}_${index}`} in={checked}>
                      <div className="flex items-center gap-2 leading-5">
                        {getStatusIcon(agentStatus === 'done' ? agentStatus : item.status)}
                        <Markdown className="text-text-secondary">{item.content}</Markdown>
                      </div>
                    </Collapse>
                  );
                } else {
                  return (
                    <Collapse key={`${item.content}_${index}`} in={checked}>
                      <TodoItem {...item} agentStatus={agentStatus} />
                    </Collapse>
                  );
                }
              })}
            </TransitionGroup>
          </div>
        ))}
        {resultData && <StepContent parsedData={parsedData} />}
      </div>
    </div>
  );
}

function getToolIcon(toolName: string) {
  switch (toolName.toLowerCase()) {
    case 'todolist':
      return <SquareCheckBig size={16} />;
    case 'websearch':
      return <Search size={16} />;
    case 'codegeneration':
      return <Terminal size={16} />;
    case 'knowledgebaseanalysis':
      return <BookOpen size={16} />;
    case 'validate':
      return <Shield size={16} />;
    default:
      return '';
  }
}

function serializeToolEvents(list: DeepThinkStatus[]) {
  const events: Record<string, Todo[]> = {};
  let todoWrites: Record<string, Todo> = {};
  list.forEach((item) => {
    if (item.toolName === 'Bash' && item.toolParams?.description) {
      events.todoList = [
        ...(events.todoList || []),
        {
          status: item.status,
          content: item.toolParams.description,
        },
      ];
    } else if (item.toolName === 'Task' && item.toolParams?.description) {
      events.todoList = [
        ...(events.todoList || []),
        {
          status: item.status,
          content: item.toolParams.description,
        },
      ];
    } else if (
      item.toolName === 'TodoWrite' &&
      item.toolParams?.todos &&
      Array.isArray(item.toolParams.todos)
    ) {
      const map = item.toolParams.todos.reduce(
        (accumulator: any, _: { content: any; status: any }) => ({
          ...accumulator,
          [_.content]: { status: _.status, content: _.content },
        }),
        {},
      );
      todoWrites = { ...todoWrites, ...map };
    } else if (item.toolName === 'WebFetch' && item.toolParams?.prompt && item.toolParams?.url) {
      events.WebSearch = [
        ...(events.WebSearch || []),
        {
          status: item.status,
          content: item.toolParams.prompt,
          url: item.toolParams.url,
        },
      ];
      // don't delete
      // } else if (item.message === 'Code Generation') {
      //   events.codeGeneration = [
      //     ...(events.codeGeneration || []),
      //     ...(
      //       item.agentCode?.files?.map((_) => ({
      //         status: item.status,
      //         fileName: _.filename || '',
      //         content: _.content || '',
      //       })) ?? []
      //     ).flat(2),
      //   ];
    } else if (item.message === 'Knowledge Graph') {
      events.knowledgeBaseAnalysis = [
        {
          status: item.status,
          content: item.knowledgeGraph || '',
        },
      ];
    } else if (item.message === 'Validate') {
      events.validate = uniqBy(
        [
          ...(events.validate || []),
          {
            status: item.status,
            content: item.content || '',
          },
        ],
        'content',
      );
    }
  });
  events.todoList = [...(events.todoList || []), ...Object.values(todoWrites)];
  return Object.fromEntries(Object.entries(events).filter(([_, v]) => v.length > 0));
}

function TodoItem(props: Todo & { agentStatus: 'doing' | 'done' | 'waiting' | 'error' }) {
  const { status, content, url, agentStatus } = props;

  // console.log('TodoItem:', content);
  if (url) {
    return (
      <div className="flex items-center gap-2 px-2 py-2.5 rounded-md transition-colors bg-[#E9E9E9] dark:bg-[#2c2c2f]">
        <div className="w-[60%] overflow-hidden gap-2 flex items-center flex-nowrap">
          <Avatar
            alt={content}
            sx={{ width: 20, height: 20 }}
            classes={{ root: 'bg-[#E9E9E9]! dark:bg-[#2c2c2f]!' }}
            src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${url}`}
          >
            <Globe size={14} className="text-text-tertiary" />
          </Avatar>
          <div className="text-text-secondary line-clamp-1">{content}</div>
        </div>
        <Link
          href={url}
          target="_blank"
          className="text-xs text-text-tertiary hover:text-[#A772FF]"
        >
          {getWebSiteDomain(url)}
        </Link>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 leading-5">
      <div>{getStatusIcon(agentStatus === 'done' ? agentStatus : status)}</div>
      <div className="text-text-secondary">{formatSentence(content)}</div>
    </div>
  );
}
